import { Head, Link } from "@inertiajs/react"
import { ChevronRight, KeyRound } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVEL_TONES,
  type AccessApp,
  type AccessGroupRow,
} from "@/types/access"

const DESCRIPTION =
  "Access groups: who can see and change each app in the Portal, as No Access, Read only or Edit."

export default function AdminAccessGroupsIndex({
  groups,
  apps,
}: {
  groups: AccessGroupRow[]
  apps: AccessApp[]
}) {
  return (
    <>
      <Head title="Groups">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Groups" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Groups"
          description="Each group gives a level on every app — No Access, Read only or Edit. Someone in several groups gets the highest. Admins always have Edit, and an override on a person's own page beats their groups."
          actions={
            <Button asChild>
              <Link href="/admin/access_groups/new" className="no-underline">
                New group
              </Link>
            </Button>
          }
        />

        <div className="mt-6">
          <FlashNotice />

          {groups.length === 0 ? (
            <div className="callout callout-signal">
              <p className="text-sm">
                There are no groups, so everybody who is not an admin has No Access to
                every app unless an override says otherwise.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {groups.map((group) => (
                <li key={group.id}>
                  <Link
                    href={`/admin/access_groups/${group.id}/edit`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <KeyRound className="h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink-display">
                          {group.name}
                        </span>
                        <Badge tone="neutral">
                          {group.member_count} {group.member_count === 1 ? "person" : "people"}
                        </Badge>
                        {group.joined_at_first_sign_in && (
                          <Badge tone="signal">Joins new accounts</Badge>
                        )}
                      </div>
                      {group.description && (
                        <div className="mt-0.5 text-xs text-ink-muted">{group.description}</div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {apps.map((app) => {
                          const level = group.levels[app.key] ?? "no_access"
                          return (
                            <Badge key={app.key} tone={ACCESS_LEVEL_TONES[level]}>
                              {app.label}: {ACCESS_LEVEL_LABELS[level]}
                            </Badge>
                          )
                        })}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </AdminShell>
    </>
  )
}
